import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  List,
  Popconfirm,
  message,
  Space,
  ColorPicker,
  Typography
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  HolderOutlined
} from '@ant-design/icons';
import { categoryService, Category } from '../../services/literature/categoryService';

const { Text } = Typography;

interface CategoryManagerProps {
  visible: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

interface SortableItemProps {
  category: Category;
  index: number;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  isDragging: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({
  category,
  index,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging
}) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, index)}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move'
      }}
    >
      <List.Item
        actions={[
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(category)}
          >
            重命名
          </Button>,
          <Popconfirm
            title="确定要删除这个分类吗？"
            description="删除后，该分类下的文献将变为未分类状态"
            onConfirm={() => onDelete(category._id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        ]}
      >
        <List.Item.Meta
          avatar={
            <div
              style={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                color: '#999'
              }}
            >
              <HolderOutlined />
            </div>
          }
          title={
            <Space>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: category.color,
                  display: 'inline-block'
                }}
              />
              <Text>{category.name}</Text>
            </Space>
          }
        />
      </List.Item>
    </div>
  );
};

const CategoryManager: React.FC<CategoryManagerProps> = ({ visible, onClose, onUpdate }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const list = await categoryService.getList();
      setCategories(list);
    } catch (error: any) {
      message.error(error.response?.data?.message || '获取分类列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchCategories();
    }
  }, [visible]);

  const handleCreate = async (values: { name: string; color: string }) => {
    try {
      await categoryService.create({
        name: values.name,
        color: values.color || '#1890ff'
      });
      message.success('分类创建成功');
      setIsCreateModalVisible(false);
      form.resetFields();
      fetchCategories();
      onUpdate();
      // 触发全局事件，通知侧边栏刷新分类列表
      window.dispatchEvent(new CustomEvent('categoryUpdated'));
    } catch (error: any) {
      message.error(error.response?.data?.message || '创建分类失败');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    editForm.setFieldsValue({
      name: category.name,
      color: category.color
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async (values: { name: string; color: string }) => {
    if (!editingCategory) return;

    try {
      await categoryService.update(editingCategory._id, {
        name: values.name,
        color: values.color || '#1890ff'
      });
      message.success('分类更新成功');
      setIsEditModalVisible(false);
      setEditingCategory(null);
      editForm.resetFields();
      fetchCategories();
      onUpdate();
      // 触发全局事件，通知侧边栏刷新分类列表
      window.dispatchEvent(new CustomEvent('categoryUpdated'));
    } catch (error: any) {
      message.error(error.response?.data?.message || '更新分类失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await categoryService.delete(id);
      message.success('分类删除成功');
      fetchCategories();
      onUpdate();
      // 触发全局事件，通知侧边栏刷新分类列表
      window.dispatchEvent(new CustomEvent('categoryUpdated'));
    } catch (error: any) {
      message.error(error.response?.data?.message || '删除分类失败');
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newCategories = [...categories];
    const [removed] = newCategories.splice(draggedIndex, 1);
    newCategories.splice(dropIndex, 0, removed);
    
    setCategories(newCategories);
    setDraggedIndex(null);

    // 更新顺序
    const orders = newCategories.map((cat, index) => ({
      id: cat._id,
      order: index
    }));

    try {
      await categoryService.updateOrders(orders);
      message.success('分类顺序已更新');
      onUpdate();
      // 触发全局事件，通知侧边栏刷新分类列表
      window.dispatchEvent(new CustomEvent('categoryUpdated'));
    } catch (error: any) {
      message.error(error.response?.data?.message || '更新分类顺序失败');
      // 恢复原来的顺序
      fetchCategories();
    }
  };

  return (
    <>
      <Modal
        title="分类管理"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalVisible(true)}
          >
            新建分类
          </Button>
        </div>

        <List
          loading={loading}
          dataSource={categories}
          renderItem={(category, index) => (
            <SortableItem
              key={category._id}
              category={category}
              index={index}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              isDragging={draggedIndex === index}
            />
          )}
        />
      </Modal>

      {/* 创建分类弹窗 */}
      <Modal
        title="新建分类"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
      >
        <Form
          form={form}
          onFinish={handleCreate}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="请输入分类名称" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="color"
            label="分类颜色"
            initialValue="#1890ff"
          >
            <ColorPicker showText />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑分类弹窗 */}
      <Modal
        title="重命名分类"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingCategory(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={editForm}
          onFinish={handleUpdate}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="请输入分类名称" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="color"
            label="分类颜色"
          >
            <ColorPicker showText />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CategoryManager;

